/**
 * iter391 — Web Push (PWA) client helper.
 *
 * Silent-fail: any failure just logs to console; existing app flows are
 * NEVER blocked. Notification permission is requested lazily after login.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function requestPushPermissionAndSubscribe(token) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return { ok: false, reason: 'unsupported' };
    if (typeof Notification === 'undefined') return { ok: false, reason: 'no_notification_api' };

    // Only prompt if user hasn't answered yet.
    if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
    if (Notification.permission === 'default') {
      const p = await Notification.requestPermission();
      if (p !== 'granted') return { ok: false, reason: p };
    }

    const reg = await navigator.serviceWorker.ready;

    // Fetch VAPID public key from backend.
    const vapidRes = await axios.get(`${API}/api/push/vapid-public-key`);
    const publicKey = vapidRes.data?.public_key;
    if (!publicKey || !vapidRes.data?.configured) return { ok: false, reason: 'no_vapid_key' };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const raw = sub.toJSON();
    await axios.post(
      `${API}/api/push/subscribe`,
      { subscription: raw },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return { ok: true };
  } catch (e) {
    // Silent-fail per constraint.
    // eslint-disable-next-line no-console
    console.warn('[push] subscribe failed (silent):', e?.message || e);
    return { ok: false, reason: 'error' };
  }
}

export async function unsubscribePush(token) {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await axios.delete(`${API}/api/push/unsubscribe`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { endpoint },
    });
  } catch (e) {
    // Silent-fail — logout still proceeds.
    // eslint-disable-next-line no-console
    console.warn('[push] unsubscribe failed (silent):', e?.message || e);
  }
}

export function setAppBadge(count) {
  try {
    if ('setAppBadge' in navigator) navigator.setAppBadge(count || 0);
  } catch (_e) { /* silent */ }
}

export function clearAppBadge() {
  try {
    if ('clearAppBadge' in navigator) navigator.clearAppBadge();
  } catch (_e) { /* silent */ }
}
